import { useEffect, useState, useRef } from "react"
import "./FlytutorPage.css"
import AngledHero from "../../components/AngledHero/AngledHero"
const API = process.env.REACT_APP_API_URL || ""

const COURSES = [
    { code: "RPC-S", title: "Small Category RPC", icon: "◈", desc: "DGCA certified for drones under 2kg. Theory, simulation and live flying included.", link: "https://flytutor.in/courses/rpc-small" },
    { code: "RPC-M", title: "Medium Category RPC", icon: "◉", desc: "Advanced certification for drones between 2–25kg. Payload and navigation training.", link: "https://flytutor.in/courses/rpc-medium" },
    { code: "RPC-SM", title: "Small & Medium Combined", icon: "◎", desc: "Full dual certification in one intensive program. Best value for serious pilots.", link: "https://flytutor.in/courses/rpc-combined" },
    { code: "RPC-UP", title: "RPC Upgradation", icon: "↑", desc: "Already certified? Upgrade your existing RPC to the next category.", link: "https://flytutor.in/courses/rpc-upgrade" },
    { code: "AERIAL", title: "Aerial Mapping & Surveying", icon: "◍", desc: "GIS, photogrammetry and LiDAR-based surveying for professionals.", link: "https://flytutor.in/courses/aerial-mapping" },
    { code: "FPV", title: "FPV Drone Training", icon: "◐", desc: "First-person view racing and freestyle flying with professional gear.", link: "https://flytutor.in/courses/fpv" },
    { code: "AGRI", title: "Agriculture Drone", icon: "◑", desc: "Precision spraying, crop monitoring and field mapping for agri-tech.", link: "https://flytutor.in/courses/agriculture" },
    { code: "PHOTO", title: "Aerial Photography & Video", icon: "◒", desc: "Cinematic drone footage, real estate and event photography.", link: "https://flytutor.in/courses/photography" },
]

const STATS = [
    { value: "350+", label: "Pilots Trained" },
    { value: "DGCA", label: "Authorized RPTO" },
    { value: "8+", label: "Course Tracks" },
]

const FACILITIES = [
    { icon: "◈", title: "Dedicated Flying Zone", desc: "Fully-equipped outdoor drone flying zone for hands-on practice." },
    { icon: "◉", title: "Simulation Centers", desc: "Advanced drone simulators for safe pre-flight training." },
    { icon: "◎", title: "Modern Labs", desc: "State-of-the-art labs for electronics, firmware and payload work." },
    { icon: "◍", title: "Expert Instructors", desc: "DGCA-certified trainers with real-world industry experience." },
]


// Facility slideshow image keys — add more in Media Manager under "flytutor" section
const FAC_IMAGES = [
    "flytutor_facility",
    "flytutor_facility_2",
    "flytutor_facility_3",
    "flytutor_facility_4",
]

function FacSlideshow({ media }) {
    const [current, setCurrent] = useState(0)
    const timerRef = useRef(null)

    const slides = FAC_IMAGES.filter(k => media[k])
    const total = slides.length

    const goTo = (idx) => {
        setTimeout(() => { setCurrent(idx) }, 320)
    }

    const next = () => { resetTimer(); goTo((current + 1) % total) }
    const prev = () => { resetTimer(); goTo((current - 1 + total) % total) }

    const resetTimer = () => {
        clearInterval(timerRef.current)
        timerRef.current = setInterval(() => setCurrent(c => (c + 1) % total), 3500)
    }

    useEffect(() => {
        if (total < 2) return
        timerRef.current = setInterval(() => setCurrent(c => (c + 1) % total), 3500)
        return () => clearInterval(timerRef.current)
    }, [total])

    if (total === 0) return (
        <div className="ft-img-placeholder">
            <span className="ft-ph-icon">◉</span>
            <span className="ft-ph-key">flytutor_facility</span>
            <span className="ft-ph-hint">Upload via Media Manager</span>
        </div>
    )

    if (total === 1) return <img src={media[slides[0]]} alt="Facility" className="ft-fac-img" />

    return (
        <div className="ft-slide-wrap">

            <button className="ft-slide-btn ft-slide-prev" onClick={prev}>
                &#8249;
            </button>

            <div className="ft-slide-track">
                <img
                    key={slides[current]}
                    src={media[slides[current]]}
                    alt={`Facility ${current + 1}`}
                    className="ft-fac-img ft-slide-img"
                />

                <div className="ft-slide-counter">
                    {current + 1} / {total}
                </div>
            </div>

            <button className="ft-slide-btn ft-slide-next" onClick={next}>
                &#8250;
            </button>

        </div>
    )
}

function useInView(threshold = 0.12) {
    const ref = useRef(null)
    const [vis, setVis] = useState(false)
    useEffect(() => {
        const o = new IntersectionObserver(([e]) => { setVis(e.isIntersecting) }, { threshold })
        if (ref.current) o.observe(ref.current)
        return () => o.disconnect()
    }, [])
    return [ref, vis]
}

function CountUp({ to, suffix = '', duration = 2800 }) {
    const [val, setVal] = useState(0)
    const [ref, vis] = useInView(0.5)
    useEffect(() => {
        if (!vis) { setVal(0); return }
        const isNum = !isNaN(parseInt(to))
        if (!isNum) { setVal(to); return }
        const target = parseInt(to)
        let start = 0
        const step = target / (duration / 16)
        const t = setInterval(() => {
            start += step
            if (start >= target) { setVal(target); clearInterval(t) }
            else setVal(Math.floor(start))
        }, 16)
        return () => clearInterval(t)
    }, [vis])
    const display = isNaN(parseInt(to)) ? to : `${val}${suffix}`
    return <span ref={ref}>{display}</span>
}

function CourseCard({ c, index, inView }) {
    const ref = useRef(null)
    const handleMouseMove = (e) => {
        const el = ref.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * 14
        const y = ((e.clientY - rect.top) / rect.height - 0.5) * 14
        el.style.transform = `perspective(600px) rotateX(${-y}deg) rotateY(${x}deg) translateY(-6px)`
        el.style.boxShadow = `${-x}px ${y}px 32px rgba(201,168,76,0.15), 0 16px 40px rgba(0,0,0,0.08)`
        el.querySelector('.ft-card-shine').style.background =
            `radial-gradient(circle at ${e.clientX - rect.left}px ${e.clientY - rect.top}px, rgba(255,255,255,0.12), transparent 60%)`
    }
    const handleMouseLeave = () => {
        const el = ref.current
        if (!el) return
        el.style.transform = ''
        el.style.boxShadow = ''
        el.querySelector('.ft-card-shine').style.background = ''
    }
    return (
        <a
            ref={ref}
            href={c.link}
            target="_blank"
            rel="noreferrer"
            className={`ft-course-card ${inView ? 'ft-card-in' : ''}`}
            style={{ transitionDelay: `${index * 0.13}s` }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <div className="ft-card-shine" />
            <div className="ft-course-top">
                <span className="ft-course-icon">{c.icon}</span>
                <span className="ft-course-code">{c.code}</span>
            </div>
            <div className="ft-course-title">{c.title}</div>
            <div className="ft-course-desc">{c.desc}</div>
            <div className="ft-course-arrow">→</div>
            <div className="ft-course-bar" />
        </a>
    )
}

export default function FlytutorPage() {
    const [media, setMedia] = useState({})
    const [heroIn, setHeroIn] = useState(false)
    const [mousePos, setMousePos] = useState({ x: 50, y: 50 })

    const [coursesRef, coursesVis] = useInView()
    const [facRef, facVis] = useInView()
    const [ctaRef, ctaVis] = useInView()
    const [statsRef, statsVis] = useInView(0.3)

    useEffect(() => {


        // Update SEO metadata
        document.title = "Flytutor | DGCA Authorized Drone Pilot Training Institute";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Flytutor by Aviatricks: India's premier DGCA-authorized drone pilot training institute. Get certified for small and medium category drones with expert instructors.");
        }

        fetch(`${API}/api/media/flytutor`).then(r => r.json()).then(d => setMedia(d)).catch(() => { })
        setTimeout(() => setHeroIn(true), 80)
    }, [])

    const handleHeroMouse = (e) => {
        const rect = e.currentTarget.getBoundingClientRect()
        setMousePos({
            x: ((e.clientX - rect.left) / rect.width) * 100,
            y: ((e.clientY - rect.top) / rect.height) * 100,
        })
    }

    return (
        <>
            <AngledHero />
            <div className="ft-page">

                {/* ── HERO ── */}
                <section className="ft-hero" onMouseMove={handleHeroMouse}>
                    <div className="ft-hero-mesh" />
                    <div className="ft-hero-dots" />
                    {/* Mouse-follow radial glow */}
                    <div className="ft-hero-follow"
                        style={{ background: `radial-gradient(circle 500px at ${mousePos.x}% ${mousePos.y}%, rgba(201,168,76,0.07), transparent 70%)` }}
                    />

                    {/* Floating orbs */}
                    <div className="ft-orb ft-orb-1" />
                    <div className="ft-orb ft-orb-2" />
                    <div className="ft-orb ft-orb-3" />

                    <div className={`ft-container ft-hero-inner ${heroIn ? 'ft-hero-in' : ''}`}>
                        <div className="ft-hero-content">
                            <div className="ft-badge">
                                <span className="ft-badge-dot" />
                                DGCA Authorized RPTO
                            </div>

                            <h1 className="ft-hero-title">
                                <span className="ft-hl ft-hl-1">Train to Fly.</span>
                                <span className="ft-hl ft-hl-2">
                                    <span className="ft-gradient-text">Certified</span>
                                    <span className="ft-white-text"> to Soar.</span>
                                </span>
                            </h1>

                            <p className="ft-hero-sub">
                                Flytutor is India's pioneer in drone training — offering DGCA-certified courses,
                                expert instructors, modern labs and real flying experience.
                            </p>

                            <div className="ft-hero-actions">
                                <a href="https://flytutor.in" target="_blank" rel="noreferrer" className="ft-btn-primary">
                                    <span>Visit Flytutor.in</span>
                                    <span className="ft-btn-arrow">→</span>
                                </a>
                                <a href="#courses" className="ft-btn-ghost">Explore Courses</a>
                            </div>
                        </div>

                        <div className="ft-hero-right">
                            {/* Cert card with tilt */}
                            <div className="ft-cert-wrap">
                                <div className="ft-cert-card">
                                    <div className="ft-cert-glow" />
                                    <div className="ft-cert-header">
                                        <div className="ft-cert-logo-row">
                                            <div className="ft-cert-emblem">✦</div>
                                            <div>
                                                <div className="ft-cert-org">DGCA · INDIA</div>
                                                <div className="ft-cert-type">Remote Pilot Certificate</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ft-cert-body">
                                        <div className="ft-cert-field"><span className="ft-cert-label">Licence Type</span><span className="ft-cert-value">RPC — Category A2</span></div>
                                        <div className="ft-cert-field"><span className="ft-cert-label">Issued by</span><span className="ft-cert-value">Directorate General of Civil Aviation</span></div>
                                        <div className="ft-cert-field"><span className="ft-cert-label">Training Centre</span><span className="ft-cert-value">Flytutor — Aviatricks Aero Lab</span></div>
                                        <div className="ft-cert-divider" />
                                        <div className="ft-cert-status">
                                            <span className="ft-cert-status-dot" />
                                            VALID · GOVERNMENT APPROVED
                                        </div>
                                    </div>
                                    <div className="ft-cert-wm">✦</div>
                                </div>
                                {/* Floating stat chips */}
                                <div className="ft-chip-float ft-cf-1">
                                    <span className="ft-cf-val">350+</span>
                                    <span className="ft-cf-lbl">Pilots Licensed</span>
                                </div>
                                <div className="ft-chip-float ft-cf-2">
                                    <span className="ft-cf-val">100%</span>
                                    <span className="ft-cf-lbl">Pass Rate</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Animated stats bar */}
                    <div className="ft-stats-bar" ref={statsRef}>
                        <div className="ft-container ft-stats">
                            {STATS.map((s, i) => (
                                <div key={i} className={`ft-stat ${statsVis ? 'ft-stat-in' : ''}`} style={{ transitionDelay: `${i * 0.18}s` }}>
                                    <div className="ft-stat-value">
                                        <CountUp to={s.value.replace(/[^0-9]/g, '') || s.value}
                                            suffix={s.value.replace(/[0-9]/g, '')} />
                                    </div>
                                    <div className="ft-stat-label">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── COURSES ── */}
                <section className="ft-section" id="courses" ref={coursesRef}>
                    <div className="ft-container">
                        <div className="ft-section-head">
                            {/* Animated eyebrow line */}
                            <div className={`ft-eye-wrap ${coursesVis ? 'ft-eye-in' : ''}`}>
                                <div className="ft-eye-line" />
                                <span className="ft-eye">TRAINING PROGRAMS</span>
                            </div>
                            <h2 className={`ft-section-h2 ${coursesVis ? 'ft-h2-in' : ''}`}>Courses We Offer</h2>
                            <p className={`ft-section-p ${coursesVis ? 'ft-p-in' : ''}`}>
                                From beginner to professional — every drone career path starts here.
                            </p>
                        </div>
                        <div className="ft-courses-grid">
                            {COURSES.map((c, i) => (
                                <CourseCard key={c.code} c={c} index={i} inView={coursesVis} />
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── FACILITIES ── */}
                <section className="ft-section ft-section-dark" ref={facRef}>
                    <div className="ft-section-dark-glow" />
                    <div className="ft-container ft-fac-inner">
                        <div className={`ft-fac-visual ${facVis ? 'ft-fac-img-in' : ''}`}>
                            <FacSlideshow media={media} />
                            <div className="ft-img-frame ft-if-tl" />
                            <div className="ft-img-frame ft-if-br" />
                        </div>

                        <div className={`ft-fac-content ${facVis ? 'ft-fac-in' : ''}`}>
                            <div className="ft-eye-wrap ft-eye-in">
                                <div className="ft-eye-line ft-eye-line-gold" />
                                <span className="ft-eye ft-eye-light">INFRASTRUCTURE</span>
                            </div>
                            <h2 className="ft-section-h2 ft-h2-light ft-h2-in">World-Class<br />Training Facilities</h2>
                            <p className="ft-section-p ft-p-light ft-p-in">Our facilities match industry standards — from simulation labs to dedicated outdoor flying zones.</p>

                            <div className="ft-fac-list">
                                {FACILITIES.map((f, i) => (
                                    <div
                                        key={f.title}
                                        className={`ft-fac-item ${facVis ? 'ft-fac-item-in' : ''}`}
                                        style={{ transitionDelay: `${0.25 + i * 0.18}s` }}
                                    >
                                        <div className="ft-fac-icon-wrap">
                                            <span className="ft-fac-icon">{f.icon}</span>
                                        </div>
                                        <div>
                                            <div className="ft-fac-title">{f.title}</div>
                                            <div className="ft-fac-desc">{f.desc}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── CTA ── */}
                <section className="ft-section ft-cta-section" ref={ctaRef}>
                    <div className="ft-container">
                        <div className={`ft-cta-box ${ctaVis ? 'ft-cta-in' : ''}`}>
                            <div className="ft-cta-bg-lines" />
                            <div className="ft-badge ft-cta-badge">
                                <span className="ft-badge-dot" />
                                Start Your Journey
                            </div>
                            <h2 className="ft-cta-h2">Ready to Become a<br />Certified Drone Pilot?</h2>
                            <p className="ft-cta-p">Get DGCA certified and launch your drone career with India's most trusted training institute.</p>
                            <a href="https://flytutor.in" target="_blank" rel="noreferrer" className="ft-btn-primary ft-btn-lg">
                                <span>Enroll at Flytutor.in</span>
                                <span className="ft-btn-arrow">→</span>
                            </a>
                        </div>
                    </div>
                </section>

            </div>
        </>
    )
}